import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '../lib/supabase';

function getSessionId() {
     let id = sessionStorage.getItem('hm_session');
     if (!id) {
          id = crypto.randomUUID();
          sessionStorage.setItem('hm_session', id);
     }
     return id;
}

function getDevice() {
     const w = window.innerWidth;
     if (w < 768) return 'mobile';
     if (w < 1024) return 'tablet';
     return 'desktop';
}

function getElInfo(el) {
     const tag = el.tagName?.toLowerCase() || '';
     const id = el.id || '';
     let text = '';
     if (el.textContent) {
          text = el.textContent.trim().substring(0, 50);
     }
     if (el.alt) text = el.alt;
     if (el.title) text = el.title;
     return { el: tag, elId: id, elText: text };
}

// Generate anonymous visitor fingerprint from non-identifying device traits
async function getVisitorId() {
     let id = localStorage.getItem('hm_visitor');
     if (id) return id;

     const raw = [
          window.screen.width,
          window.screen.height,
          navigator.language,
          Intl.DateTimeFormat().resolvedOptions().timeZone,
          navigator.platform,
          navigator.hardwareConcurrency || '',
     ].join('|');

     const encoded = new TextEncoder().encode(raw);
     const hash = await crypto.subtle.digest('SHA-256', encoded);
     id = Array.from(new Uint8Array(hash)).map(b => b.toString(16).padStart(2, '0')).join('');
     localStorage.setItem('hm_visitor', id);
     return id;
}

function parseBrowser() {
     const ua = navigator.userAgent;
     let browser = 'Unknown';
     let os = 'Unknown';

     // Browser
     if (ua.includes('Firefox/')) {
          browser = 'Firefox ' + (ua.match(/Firefox\/(\d+)/)?.[1] || '');
     } else if (ua.includes('Edg/')) {
          browser = 'Edge ' + (ua.match(/Edg\/(\d+)/)?.[1] || '');
     } else if (ua.includes('Chrome/') && !ua.includes('Edg/')) {
          browser = 'Chrome ' + (ua.match(/Chrome\/(\d+)/)?.[1] || '');
     } else if (ua.includes('Safari/') && !ua.includes('Chrome/')) {
          browser = 'Safari ' + (ua.match(/Version\/(\d+)/)?.[1] || '');
     }

     // OS
     if (ua.includes('Windows NT')) os = 'Windows';
     else if (ua.includes('Mac OS X')) os = 'macOS';
     else if (ua.includes('Android')) os = 'Android';
     else if (/iPhone|iPad|iPod/.test(ua)) os = 'iOS';
     else if (ua.includes('Linux')) os = 'Linux';

     return { browser: browser.trim(), os };
}

function getUTMParams() {
     let params = sessionStorage.getItem('hm_utm');
     if (params) return JSON.parse(params);

     const url = new URLSearchParams(window.location.search);
     const utm = {
          utm_source: url.get('utm_source') || null,
          utm_medium: url.get('utm_medium') || null,
          utm_campaign: url.get('utm_campaign') || null,
     };
     sessionStorage.setItem('hm_utm', JSON.stringify(utm));
     return utm;
}

function getReferrer() {
     let ref = sessionStorage.getItem('hm_referrer');
     if (ref !== null) return ref || null;

     // Strip query params for privacy, store only origin + path
     try {
          if (document.referrer) {
               const url = new URL(document.referrer);
               ref = url.origin + url.pathname;
          } else {
               ref = '';
          }
     } catch {
          ref = '';
     }
     sessionStorage.setItem('hm_referrer', ref);
     return ref || null;
}

export default function HeatmapTracker() {
     const location = useLocation();
     const maxScrollRef = useRef(0);
     const pageRef = useRef('');
     const clickBufferRef = useRef([]);
     const flushTimerRef = useRef(null);

     // Flush click buffer to Supabase in batches
     function flushClicks() {
          const clicks = clickBufferRef.current.splice(0);
          if (clicks.length === 0) return;
          supabase.from('heatmap_clicks').insert(clicks).then(() => {});
     }

     // Save scroll depth on page leave
     function saveScroll() {
          if (maxScrollRef.current > 0 && pageRef.current) {
               const depth = Math.min(Math.round(maxScrollRef.current), 100);
               supabase.from('heatmap_scroll').insert({
                    session_id: getSessionId(),
                    page: pageRef.current,
                    max_depth: depth,
                    device: getDevice(),
               }).then(() => {});
          }
     }

     // Track page view via edge function
     async function trackPageView(pageName) {
          try {
               const visitorId = await getVisitorId();
               const { browser, os } = parseBrowser();
               const utm = getUTMParams();
               const referrer = getReferrer();

               const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
               const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

               fetch(`${supabaseUrl}/functions/v1/track-pageview`, {
                    method: 'POST',
                    headers: {
                         'Content-Type': 'application/json',
                         'Authorization': `Bearer ${supabaseKey}`,
                         'apikey': supabaseKey,
                    },
                    body: JSON.stringify({
                         session_id: getSessionId(),
                         visitor_id: visitorId,
                         page: pageName,
                         referrer,
                         ...utm,
                         browser,
                         os,
                         device: getDevice(),
                         screen_w: window.screen.width,
                         screen_h: window.screen.height,
                    }),
               });
          } catch {
               // Fire-and-forget — don't block the UI
          }
     }

     useEffect(() => {
          // Flag admin users so we never track their visits
          if (location.pathname.startsWith('/admin')) {
               localStorage.setItem('hm_is_admin', '1');
               return;
          }

          // Don't track admin users on any page
          if (localStorage.getItem('hm_is_admin')) return;

          // Save scroll from previous page
          saveScroll();

          // Reset for new page
          const pageName = location.pathname === '/' ? 'home' : location.pathname.replace(/^\//, '').replace(/\//g, '-');
          pageRef.current = pageName;
          maxScrollRef.current = 0;

          const sessionId = getSessionId();
          const device = getDevice();

          // Track page view
          trackPageView(pageName);

          // Click handler
          function handleClick(e) {
               const info = getElInfo(e.target);
               clickBufferRef.current.push({
                    session_id: sessionId,
                    page: pageRef.current,
                    x: Math.round(e.pageX),
                    y: Math.round(e.pageY),
                    dw: window.innerWidth,
                    dh: document.documentElement.scrollHeight,
                    el: info.el,
                    el_id: info.elId,
                    el_text: info.elText,
                    device,
               });

               // Flush every 5 clicks or after 3 seconds
               if (clickBufferRef.current.length >= 5) {
                    flushClicks();
               }
          }

          // Scroll handler
          function handleScroll() {
               const scrollTop = window.scrollY || document.documentElement.scrollTop;
               const docHeight = document.documentElement.scrollHeight - window.innerHeight;
               if (docHeight > 0) {
                    const pct = (scrollTop / docHeight) * 100;
                    if (pct > maxScrollRef.current) {
                         maxScrollRef.current = pct;
                    }
               }
          }

          document.addEventListener('click', handleClick);
          window.addEventListener('scroll', handleScroll, { passive: true });

          // Flush timer
          flushTimerRef.current = setInterval(flushClicks, 3000);

          // Save on page unload
          function handleUnload() {
               flushClicks();
               saveScroll();
          }
          window.addEventListener('beforeunload', handleUnload);

          return () => {
               document.removeEventListener('click', handleClick);
               window.removeEventListener('scroll', handleScroll);
               window.removeEventListener('beforeunload', handleUnload);
               clearInterval(flushTimerRef.current);
               flushClicks();
          };
          // eslint-disable-next-line react-hooks/exhaustive-deps
     }, [location.pathname]);

     return null;
}
